import { Controller } from "@hotwired/stimulus";

// Zaalplan v2 in de red ticketshop: interactieve stoelenkiezer (Konva) i.p.v. de
// server-gegenereerde PNG. Zelfde flow als v1: je aantallen zijn al gekozen en
// automatisch toegewezen; klik op een vrije stoel verplaatst je hele groep
// (server hergebruikt SeatFinder + locking via het update_seating_coordinates-endpoint).
export default class extends Controller {
  static targets = ["canvas", "popover", "resetBtn"];

  // "Herstel beste plekken" hoort pas te verschijnen zodra je de automatische toewijzing hebt
  // aangepast (een stoel/rij verplaatst). Bij de default-toewijzing is er niets te herstellen.
  showResetBtn(show) { if (this.hasResetBtnTarget) this.resetBtnTarget.hidden = !show; }
  static values = { updateUrl: String, uuid: String };

  // mobiel: de "Jouw stoelen"-onderbalk in-/uitklappen (compacte samenvatting <-> volledige lijst).
  // Op desktop staat de lijst altijd open (CSS), dus deze toggle raakt daar niets.
  toggleSheet(e) {
    const panel = e.currentTarget.closest(".sp2-panel");
    if (panel) panel.classList.toggle("is-open");
  }

  async connect() {
    // AT-1113: terug met de knop van de browser haalt deze pagina uit de bfcache -- hij komt
    // terug precies zoals hij was, uit het geheugen, zonder dat de server er iets van merkt.
    // Op een zaalplan is dat een momentopname van wie er toen vrij was: iemand anders kan
    // intussen stoel 12 gekocht hebben, en dan klik je op een plek die er vrij uitziet en
    // krijg je een foutmelding terug.
    //
    // Niet herladen maar de stoelen opnieuw ophalen. Gemeten op dit plan van 4000 stoelen is
    // een volledige herlading 2,3 seconden en bijna een megabyte, en refreshSeats 0,55
    // seconde en de helft daarvan -- en die laatste houdt je zoomniveau en je scrollpositie,
    // die bij een herlading allebei weg zijn.
    //
    // persisted onderscheidt een herstel uit de cache van een gewone lading. De spike-guard
    // is nodig omdat Konva hier dynamisch geladen wordt: vuurt pageshow voordat die import
    // klaar is, dan is er nog geen plan om stoelen in te zetten.
    this._verversUitCache = (event) => {
      if (!event.persisted || !this.spike) return;

      this.refreshSeats().catch(() => {});
    };
    window.addEventListener("pageshow", this._verversUitCache);

    // reserveer de canvas-hoogte + toon een loader VÓÓR de (async) Konva-import, zodat de pagina niet
    // verspringt en het plan niet ineens "inklapt" zodra Konva laadt.
    this._reserveCanvasHeight();
    // dynamic import: Konva + seatplan-lib alleen laden op de zaalplan-pagina
    const { SeatplanSpike } = await import("libraries/seatplan-spike.js");
    const d = this.canvasTarget.dataset;
    this.ranks = JSON.parse(d.ranks || "[]");
    this.ranksById = Object.fromEntries(this.ranks.map((r) => [r.id, r]));
    this.spike = new SeatplanSpike({
      mode: "viewer",
      shopMode: true,
      container: this.canvasTarget.id,
      colors: JSON.parse(d.colors || "{}"),
      sections: JSON.parse(d.sections || "[]"),
      seats: JSON.parse(d.seats || "[]"),
      objects: JSON.parse(d.objects || "[]"),
      groups: JSON.parse(d.groups || "{}"), // opmaak-meta per sectie-groep (zones)
      orphans: JSON.parse(d.orphans || "[]"), // vrije stoelen die door je selectie alleen komen te zitten
      showAllSeats: d.showAllSeats, // volgt de zaal-instelling "Stoelen in overzicht"
      planBgColor: d.planBg || null, // instelbare achtergrondkleur van het zaalplan (uit de editor)
    });
    // verplaatsen: sleep een stoel (één) of de rij-greep (hele rij) naar een lege plek
    this.spike.onShopSeatDrop = (fromIds, toId) => this.dropSeats(fromIds, toId);
    // melding vanuit de spike (bv. loep-drop naar een vak zonder genoeg ruimte)
    this.spike.onShopMessage = (msg) => this.warn(msg);
    // meng-loep (meerdere rangen) naar een ander vak gesleept -> kies welke rang mee gaat
    this.spike.onShopMagChoice = (options, secName) => this.showMagRankChoice(options, secName);
    // loep naar een ander vak: de server kiest de beste stoelen in dat vak (rij-prioriteit-DP)
    this.spike.onShopMagMove = (fromIds, sectionId) => this.moveSeatsToSection(fromIds, sectionId);
    // klik op een lege plek met genoeg ruimte = je hele rij springt daarheen (alternatief voor slepen)
    this.spike.onShopFreeClick = (seatId) => this.freeSeatClick(seatId);
    // TOUCH-model (tik-oppakken -> tik-plaatsen): eigen stoel/rij oppakken, dan een vrije plek tikken
    this.spike.onShopPickupSeat = (id) => this.pickup([id], "seat");
    this.spike.onShopPickupRow = (ids) => this.pickup(ids, "row");
    this.spike.onShopFreeTap = (id) => this.freeTap(id);
    this.spike.onShopTapEmpty = () => this.tapEmpty();
    // tik op je eigen stoel (touch) -> keuzemenu "deze stoel / hele rij"
    this.spike.onShopSeatMenu = (id, blockIds) => this.showSeatMenu(id, blockIds);
    // tooltip op de rij-greep
    this.spike.onHandleTip = (text, x, y) => this.showTip(text, x, y);
    this.spike.onHandleTipLeave = () => this.hideTip();
    this.spike.onSeatHover = (seat, x, y) => this.showPopover(seat, x, y);
    this.spike.onSeatLeave = () => this.hidePopover();

    // kaartjes in de zijkolom lichtjes highlighten wanneer hun stoelen in beeld zijn
    this.spike.onViewChange = () => this.updateFocusHighlight();

    // klik op een tickets-kaartje in de zijkolom = naar die rang-groep zoomen
    // (delegation: blijft werken nadat renderTickets de kaartjes ververst)
    const ticketsBox = document.getElementById("sp2-my-tickets");
    if (ticketsBox) {
      ticketsBox.addEventListener("click", (e) => {
        // klik op een stoelregel -> strak naar die stoel; klik elders op de kaart -> naar de rang
        const row = e.target.closest("[data-seat-id]");
        const card = e.target.closest("[data-rank]");
        // mobiel: de "Jouw stoelen"-sheet inklappen zodat je de kaart ziet inzoomen naar de keuze
        if (row || card) {
          const panel = ticketsBox.closest(".sp2-panel");
          if (panel) panel.classList.remove("is-open");
        }
        if (row) { this.focusSeat(parseInt(row.dataset.seatId, 10)); return; }
        if (card) this.focusRank(parseInt(card.dataset.rank, 10));
      });
      // hover over een stoel-regel in de lijst -> die stoel op de kaart oplichten
      // (delegation, blijft werken na een live-refresh van de lijst)
      ticketsBox.addEventListener("mouseover", (e) => {
        const row = e.target.closest("[data-seat-id]");
        const id = row ? parseInt(row.dataset.seatId, 10) : null;
        if (id === this._hoverSeatId) return;
        this._hoverSeatId = id;
        if (!this.spike) return;
        if (id != null) this.spike.pulseSeat(id); else this.spike.unpulseSeat();
      });
      ticketsBox.addEventListener("mouseleave", () => {
        this._hoverSeatId = null;
        if (this.spike) this.spike.unpulseSeat();
      });
    }

    // sectie-badges: tik op een badge = naar die sectie zoomen (delegation, blijft werken na herbouw)
    const tabs = document.getElementById("sp2-section-tabs");
    if (tabs) {
      tabs.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-section-id]");
        if (!btn || !this.spike) return;
        const secId = parseInt(btn.dataset.sectionId, 10);
        if (secId !== this.spike.activeSectionId) this.spike.zoomToSection(secId);
      });
    }

    // NIET meer automatisch naar je stoelen inzoomen: dan zag je de zaal nooit. In plaats daarvan
    // blijft het OVERZICHT staan (fitShopStage in initShopNav) met een vergrootglas per sectie waar je
    // stoelen hebt (seats.io-model, spike._drawMineMagnifiers). Klik/tik daarop = naar je stoelen vliegen.
    const mine = (JSON.parse(d.seats || "[]")).find((s) => s.state === "mine");
    this.mineSectionId = mine ? mine.section_id : null;

    this.updateOrphans(JSON.parse(d.orphans || "[]")); // waarschuwing bij eerste render

    // Esc: eerst een open groep-keuzemenu sluiten; anders terug naar het hele-zaal-overzicht
    this._onKeydown = (e) => {
      if (e.key !== "Escape") return;
      const gp = document.getElementById("sp2-group-picker");
      if (gp && !gp.hidden) { this._hideGroupPicker(); return; }
      this.back();
    };
    window.addEventListener("keydown", this._onKeydown);

    // Konva heeft nu de echte hoogte gezet en het plan is getekend -> loader weg en canvas soepel infaden.
    this._revealCanvas();
    // op een klein scherm eenmalig aanbieden om het zaalplan groter te openen
    setTimeout(() => this.maybeOfferExpand(), 700);
  }

  disconnect() {
    if (this._verversUitCache) window.removeEventListener("pageshow", this._verversUitCache);
    if (this._onKeydown) window.removeEventListener("keydown", this._onKeydown);
    document.body.classList.remove("sp2-expanded"); // vergroot-modus niet laten hangen na navigatie
    this._hideExpandPrompt();
  }

  // reserveer de canvas-hoogte vóór Konva laadt (zelfde formule als spike.fitShopStage), zodat de
  // "Jouw stoelen"-balk en de content eronder niet verspringen als de canvas z'n hoogte krijgt.
  _reserveCanvasHeight() {
    const el = this.canvasTarget;
    const wrap = el.closest(".sp2-canvas-wrap") || el.parentElement;
    if (wrap) wrap.classList.add("is-loading");
    const top = wrap ? wrap.getBoundingClientRect().top : 0;
    let h;
    if (window.innerWidth < 768) {
      const panel = document.querySelector(".sp2-panel");
      const bottomReserve = 58 + (panel ? panel.offsetHeight : 60) + 12;
      h = Math.max(300, window.innerHeight - Math.max(top, 0) - 16 - bottomReserve);
    } else {
      const w = (el.parentElement && el.parentElement.clientWidth) || 700;
      h = Math.max(360, Math.min(w, window.innerHeight - 240));
    }
    el.style.minHeight = `${Math.round(h)}px`;
  }

  // loader weg + canvas infaden zodra het plan staat; de gereserveerde min-height los (Konva heeft nu
  // de echte hoogte gezet, dus het wrapt precies zonder gat).
  _revealCanvas() {
    const el = this.canvasTarget;
    const wrap = el.closest(".sp2-canvas-wrap") || el.parentElement;
    requestAnimationFrame(() => {
      el.style.minHeight = "";
      if (wrap) { wrap.classList.remove("is-loading"); wrap.classList.add("is-ready"); }
    });
  }

  // Live weergave van lege stoelen die de selectie los laat: rode markering op de kaart (engine),
  // een rood lijstje in de rechterkolom (welke stoel + waarom), en "Verder" geblokkeerd zolang
  // ze er zijn (server blokkeert het ook als vangnet). Klikken/schuiven blijft vrij.
  updateOrphans(orphans) {
    orphans = orphans || [];
    const count = orphans.length;
    this._orphanCount = count;

    // zwevend rood balkje bovenin het zaalplan (niet in de rechterkolom). Op mobiel dekt de volle
    // melding het canvas af, daarom kun je 'm inklappen tot een klein rood chipje (en weer uitklappen).
    if (count > 0) {
      const loose = orphans.filter((o) => o.type === "mine"); // jouw stoel zit alleen
      const empty = orphans.filter((o) => o.type !== "mine"); // lege stoel blijft los achter
      const where = (list) => list.map((o) => `Rij ${o.row ?? "?"}·${o.nr ?? "?"}`).join(", ");
      const parts = [];
      if (loose.length) {
        parts.push(`${loose.length === 1 ? "1 losse stoel" : `${loose.length} losse stoelen`} (${where(loose)})`);
      }
      if (empty.length) {
        parts.push(`${empty.length === 1 ? "1 lege plek blijft over" : `${empty.length} lege plekken blijven over`} (${where(empty)})`);
      }
      this._orphanSeatIds = orphans.map((o) => o.id); // voor de "Bekijk"-knop
      this._orphanMsg = `${parts.join(" · ")}. Plaats je stoelen naast elkaar om verder te gaan.`;
    } else {
      this._orphanSeatIds = [];
      this._orphanMsg = "";
      this._orphanCollapsed = false; // volgende keer weer volledig tonen
    }
    this._renderOrphanBanner();

    // AT-1113: er is er meer dan een. Op een breed scherm staat de hoofdknop in "Jouw
    // stoelen", op telefoon in de vaste onderbalk; ze staan allebei in de DOM en CSS
    // verbergt er een. De blokkade hoort dus op allebei, anders is hij te omzeilen door
    // het venster te verbreden.
    const proceeds = this._proceedButtons();
    if (proceeds.length && !this._proceedGuard) {
      this._proceedGuard = (e) => {
        if (e.currentTarget.classList.contains("is-disabled")) { e.preventDefault(); e.stopImmediatePropagation(); }
      };
      proceeds.forEach((knop) => knop.addEventListener("click", this._proceedGuard, true));
    }
    this._syncProceed();
  }

  _proceedButtons() {
    return Array.from(document.querySelectorAll("[data-sp2-proceed]"));
  }

  // "Verder" is geblokkeerd zolang er losse stoelen zijn OF een verplaatsing nog niet is afgerond,
  // zodat je niet naar de checkout kunt terwijl de server de move nog verwerkt (stale cart).
  _syncProceed() {
    const blocked = (this._orphanCount || 0) > 0 || !!this.busy;
    this._proceedButtons().forEach((knop) => {
      knop.classList.toggle("is-disabled", blocked);
      knop.setAttribute("aria-disabled", blocked ? "true" : "false");
    });
  }

  // busy-vlag centraal zetten zodat de "Verder"-knop meteen mee-blokkeert tijdens een verplaatsing
  setBusy(on) { this.busy = !!on; this._syncProceed(); }

  // welk tickets-kaartje hoort bij wat er nu in beeld is? Van de groepen die volledig
  // binnen het canvas vallen wint alléén de groep het dichtst bij het midden van de
  // view (de echte focus) — anders blijven er twee tegelijk opgelicht staan
  updateFocusHighlight() {
    const box = document.getElementById("sp2-my-tickets");
    if (!box || !this.spike) return;
    // opgepakte stoel(en) losraken zodra je uit de sectie terug naar het overzicht gaat
    if (this._pickup && this.spike.activeSectionId == null) this.cancelPickup();
    const st = this.spike.stage;
    const zoomedIn = st.scaleX() > this.spike.baseScale * 1.15;
    const cards = Array.from(box.querySelectorAll("[data-rank]"));
    let best = null;
    let bestDist = Infinity;
    if (zoomedIn) {
      cards.forEach((card) => {
        const rankId = parseInt(card.dataset.rank, 10);
        const seats = this.spike.seats.filter((s) => s.state === "mine" && s.rank_id === rankId);
        if (!seats.length) return;
        const pts = seats.map((s) => {
          const p = this.spike.seatViewPos(s);
          return { x: p.x * st.scaleX() + st.x(), y: p.y * st.scaleY() + st.y() };
        });
        if (!pts.every((p) => p.x >= 0 && p.x <= st.width() && p.y >= 0 && p.y <= st.height())) return;
        const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
        const cy = pts.reduce((a, p) => a + p.y, 0) / pts.length;
        const dist = Math.hypot(cx - st.width() / 2, cy - st.height() / 2);
        if (dist < bestDist) { bestDist = dist; best = card; }
      });
    }
    cards.forEach((c) => c.classList.toggle("is-focused", c === best));
    this.renderSectionTabs();
  }

  // sectie-badges bovenaan het zaalplan: de secties waarin je stoelen hebt (met aantal) + de sectie
  // waar je nu in kijkt. Actieve sectie opgelicht. Alleen herbouwen als er iets wijzigt (onViewChange
  // vuurt ook tijdens pannen/slepen -> anders zou de innerHTML elke frame herschreven worden).
  renderSectionTabs() {
    const box = document.getElementById("sp2-section-tabs");
    if (!box || !this.spike) return;
    const active = this.spike.activeSectionId;
    // op het OVERZICHT geen tabs: de vergrootglazen tonen daar al waar je stoelen zitten (en ze lagen
    // bovenop de loepen). De tabs zijn er om te WISSELEN als je ingezoomd bent.
    if (active == null) { if (!box.hidden) { box.hidden = true; box.innerHTML = ""; } this._tabsSig = ""; return; }
    const counts = {};
    this.spike.seats.forEach((s) => { if (s.state === "mine") counts[s.section_id] = (counts[s.section_id] || 0) + 1; });
    const ids = Object.keys(counts).map((k) => parseInt(k, 10));
    if (active != null && !ids.includes(active)) ids.push(active);
    if (!ids.length) { if (!box.hidden) { box.hidden = true; box.innerHTML = ""; } this._tabsSig = ""; return; }
    const order = new Map(this.spike.sections.map((s, i) => [s.id, i]));
    ids.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    const sig = ids.map((id) => `${id}:${counts[id] || 0}:${id === active ? 1 : 0}`).join("|");
    if (sig === this._tabsSig) return; // niets gewijzigd -> geen herbouw (voorkomt layout-thrash bij pannen)
    this._tabsSig = sig;
    box.hidden = false;
    box.innerHTML = ids.map((id) => {
      const sec = this.spike.sections.find((s) => s.id === id);
      const name = this._esc(sec ? sec.name : "");
      const n = counts[id] || 0;
      const cls = "sp2-sectiontab" + (id === active ? " is-active" : "");
      return `<button type="button" class="${cls}" data-section-id="${id}">`
        + `<span class="sp2-sectiontab-name">${name}</span>`
        + (n ? `<span class="sp2-sectiontab-count">${n}</span>` : "")
        + `</button>`;
    }).join("");
  }

  _esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  back() { if (this.spike) this.spike.resetView(); }

  // klik op een stoel-regel: strak naar die stoel (alle-stoelen), of in het vakken-model eerst de
  // SECTIE inzoomen — daar worden de stoelen pas getekend.
  focusSeat(seatId) {
    if (!this.spike) return;
    if (this.spike.showAllSeats) this.spike.zoomToSeat(seatId);
    else this.spike.focusSeatInSection(seatId); // vakken: sectie activeren + strak op de stoel
  }

  // naar al jouw stoelen (alle rangen samen)
  focusMine() {
    if (!this.spike || this.mineSectionId == null) return;
    if (this.spike.showAllSeats) this.spike.zoomToSeats((s) => s.state === "mine");
    else this.spike.zoomToSection(this.mineSectionId);
  }

  // naar de stoelen van één rang (klik op een tickets-kaartje)
  focusRank(rankId) {
    if (!this.spike) return;
    const mine = this.spike.seats.filter((s) => s.state === "mine" && s.rank_id === rankId);
    if (!mine.length) return;
    if (this.spike.showAllSeats) this.spike.zoomToSeats((s) => s.state === "mine" && s.rank_id === rankId);
    else this.spike.zoomToSection(mine[0].section_id);
  }
  zoomIn() { if (this.spike) this.spike.zoomStep(1); }
  zoomOut() { if (this.spike) this.spike.zoomStep(-1); }

  // vergroot-modus: de zaalplan-kaart legt zich over de header/steps/titel heen zodat het canvas op
  // een klein scherm (iPhone SE e.d.) veel meer ruimte krijgt. Geen OS-fullscreen (werkt niet op iOS),
  // maar een vaste overlay binnen de pagina. Na het wisselen het canvas op de nieuwe maat brengen.
  toggleExpand() {
    const on = !document.body.classList.contains("sp2-expanded");
    document.body.classList.toggle("sp2-expanded", on);
    this._hideExpandPrompt();
    requestAnimationFrame(() => { if (this.spike) this.spike.resizeShop(); });
  }

  // eenmalige nudge op een klein scherm: "we zagen dat je scherm klein is — groter openen?"
  maybeOfferExpand() {
    if (window.innerWidth >= 430 || window.innerHeight >= 720) return; // alleen echt kleine schermen
    try { if (sessionStorage.getItem("sp2ExpandPrompt") === "seen") return; } catch (e) { /* private mode */ }
    const wrap = this.canvasTarget.closest(".sp2-canvas-wrap") || this.canvasTarget.parentElement;
    if (!wrap) return;
    const el = document.createElement("div");
    el.id = "sp2-expand-prompt";
    el.className = "sp2-expand-prompt";
    el.innerHTML =
      `<div class="sp2-expand-prompt__box">` +
      `<div class="sp2-expand-prompt__title">Klein scherm</div>` +
      `<p class="sp2-expand-prompt__text">Wil je het zaalplan groter openen voor meer ruimte?</p>` +
      `<div class="sp2-expand-prompt__actions">` +
      `<button type="button" class="sp2-expand-prompt__no" data-x>Nee, laat maar</button>` +
      `<button type="button" class="sp2-expand-prompt__yes" data-go>Ja, groter</button>` +
      `</div></div>`;
    wrap.appendChild(el);
    el.addEventListener("click", (e) => {
      if (e.target.closest("[data-go]")) { this._markExpandSeen(); this.toggleExpand(); }
      else if (e.target.closest("[data-x]") || e.target === el) { this._markExpandSeen(); this._hideExpandPrompt(); }
    });
  }
  _markExpandSeen() { try { sessionStorage.setItem("sp2ExpandPrompt", "seen"); } catch (e) { /* private mode */ } }
  _hideExpandPrompt() { const el = document.getElementById("sp2-expand-prompt"); if (el) el.remove(); }

  // teken het rode balkje op basis van _orphanMsg + de ingeklapte staat. Ingeklapt = klein rood chipje
  // (tikken klapt weer uit); uitgeklapt = volle melding met "Bekijk" + inklap-kruisje.
  _renderOrphanBanner() {
    const banner = document.getElementById("sp2-orphan-banner");
    if (!banner) return;
    if (!this._orphanMsg) { banner.hidden = true; banner.innerHTML = ""; banner.classList.remove("is-collapsed"); return; }
    banner.hidden = false;
    // onder de sectie-badges plaatsen als je ingezoomd bent (die staan ook linksboven), anders bovenaan
    const tabs = document.getElementById("sp2-section-tabs");
    const tabsBottom = (tabs && !tabs.hidden && tabs.offsetParent) ? tabs.offsetTop + tabs.offsetHeight + 6 : 0;
    banner.style.top = `${Math.max(8, tabsBottom)}px`;
    if (this._orphanCollapsed) {
      banner.classList.add("is-collapsed");
      banner.innerHTML =
        `<button type="button" class="sp2-orphan-chip" data-action="shop-seatplan-v2#expandOrphan" aria-label="Toon waarschuwing">` +
        `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>` +
        `<span class="sp2-orphan-count">${this._orphanCount || ""}</span></button>`;
    } else {
      banner.classList.remove("is-collapsed");
      banner.innerHTML =
        `<span class="sp2-orphan-msg">${this._orphanMsg}</span>` +
        `<button type="button" class="sp2-orphan-view" data-action="shop-seatplan-v2#zoomOrphans">Bekijk</button>`;
    }
  }
  expandOrphan() { this._orphanCollapsed = false; this._renderOrphanBanner(); }

  // "Bekijk" in het rode balkje: zoom strak op de probleemstoel(en). In het vakken-model eerst de
  // sectie activeren (stoelen worden daar pas getekend) en dan op de stoel inzoomen.
  zoomOrphans() {
    if (!this.spike || !this._orphanSeatIds || !this._orphanSeatIds.length) return;
    // "Bekijk" klapt de melding meteen weg naar het kleine chipje: je gaat naar het probleem toe
    // zónder dat de volle banner het canvas afdekt. Tik op het chipje om de tekst weer te lezen.
    this._orphanCollapsed = true;
    this._renderOrphanBanner();
    const ids = this._orphanSeatIds;
    if (!this.spike.showAllSeats) { this.spike.focusSeatInSection(ids[0]); return; }
    if (ids.length === 1) { this.spike.zoomToSeat(ids[0]); return; }
    const set = new Set(ids);
    this.spike.zoomToSeats((s) => set.has(s.id)); // meerdere: alles samen in beeld
  }

  // Verplaatsen door slepen: de engine roept dit aan als je een stoel (of de rij-greep) op een
  // lege plek loslaat. Zelfde rang is al gegarandeerd door de drop-detectie in de engine.
  dropSeats(fromIds, toId) {
    // GEEN busy-guard hier: bij snel achter elkaar slepen werd de tweede move gedropt terwijl de
    // optimistische update al was toegepast -> de client liep vóór op de server (verkeerde vinkjes/errors).
    // moveSeats queuet nu, dus opeenvolgende sleepbewegingen worden netjes op volgorde verstuurd.
    if (this._pickup) this.cancelPickup(); // sleep-drop vervangt de tik-plaats-flow -> pickup-balk/markering weg
    this.moveSeats(fromIds, toId);
  }

  // Klik op een lege plek: verplaats een aaneengesloten groep van je eigen stoelen (zelfde rang)
  // hierheen. Heb je MEERDERE losse groepen van die rang, dan kies je eerst welke — anders raadt de
  // shop en pakt hij (verrassend) een andere groep dan je bedoelt.
  freeSeatClick(seatId) {
    if (this.busy) return;
    this._hideGroupPicker();
    const rank = this.spike.seatRank(seatId);
    const groups = this.spike.mineGroupsOfRank(rank);
    if (!groups.length) { this.warn("Op deze plek zit een andere rang dan jouw tickets."); return; }
    if (groups.length === 1) { this._tryMoveGroup(groups[0].ids, rank, seatId); return; }
    this._showGroupPicker(groups, rank, seatId);
  }

  // verplaats één groep naar de aangeklikte plek, mits er genoeg aaneengesloten ruimte is
  _tryMoveGroup(blockIds, rank, seatId) {
    const startSeat = this.spike.seats.find((s) => s.id === seatId);
    const dest = this.spike._destinationBlock(rank, blockIds.length, startSeat);
    if (dest.length < blockIds.length) {
      this.warn(`Hier is niet genoeg ruimte voor ${blockIds.length} stoelen. Kies een plek met meer ruimte.`);
      return;
    }
    this.moveSeats(blockIds, seatId);
  }

  // ---- touch pickup-model: eerst oppakken (eigen stoel/rij), dan een vrije plek tikken ----
  // pak stoel(en) op: markeer ze en toon een balk "kies een nieuwe plek"
  pickup(ids, kind) {
    if (this.busy || !ids || !ids.length) return;
    this._hideGroupPicker();
    this._pickup = { ids: ids.slice() };
    this.spike.setPicked(ids);
    this.showPickupBar(this._pickupLabel(ids, kind));
  }

  _pickupLabel(ids, kind) {
    const seats = ids.map((id) => this.spike.seats.find((s) => s.id === id)).filter(Boolean);
    const first = seats[0];
    if (!first) return `deze ${ids.length} stoelen`;
    if (kind === "row" || ids.length > 1) return `deze ${ids.length} stoelen (rij ${first.row_label})`;
    return `stoel ${first.nr} (rij ${first.row_label})`;
  }

  // tik op een vrije plek: opgepakt? plaats daar. Niets opgepakt: verplaats de (enige) groep of vraag
  // om eerst op te pakken (voorkomt de verwarrende 'welke groep'-popup).
  freeTap(freeSeatId) {
    if (this.busy) return;
    if (this._pickup) {
      const ids = this._pickup.ids;
      const rank = this.spike.seatRank(ids[0]);
      const startSeat = this.spike.seats.find((s) => s.id === freeSeatId);
      if (startSeat && startSeat.rank_id !== rank) { this.warn("Deze plek hoort bij een andere rang. Kies een vrije plek in jouw rang."); return; }
      const dest = this.spike._destinationBlock(rank, ids.length, startSeat);
      if (dest.length < ids.length) { this.warn(`Hier is niet genoeg ruimte voor ${ids.length} stoelen. Kies een plek met meer ruimte.`); return; }
      this.cancelPickup();
      this.moveSeats(ids, freeSeatId);
      return;
    }
    const rank = this.spike.seatRank(freeSeatId);
    const groups = this.spike.mineGroupsOfRank(rank);
    if (!groups.length) { this.warn("Op deze plek zit een andere rang dan jouw tickets."); return; }
    if (groups.length === 1) { this._tryMoveGroup(groups[0].ids, rank, freeSeatId); return; }
    this.warn("Tik eerst je eigen stoel om die te verplaatsen.");
  }

  // tik op leegte: opgepakt -> annuleren; anders -> terug naar het zaaloverzicht
  tapEmpty() {
    if (this._pickup) { this.cancelPickup(); return; }
    if (this.spike.activeSectionId != null) this.spike.resetView();
  }

  cancelPickup() {
    this._pickup = null;
    this.spike.clearPicked();
    this._hideActionBar();
  }

  // tijdens het oppakken: vaste onderbalk "tik/sleep naar een vrije plek" (met annuleren)
  showPickupBar(label) {
    this._showActionBar(
      `<div class="sp2-ab-ctx">Verplaatsen: ${label}</div>` +
      `<div class="sp2-ab-row"><span class="sp2-ab-msg">Tik of sleep naar een vrije plek</span>` +
      `<button type="button" class="sp2-ab-x" data-cancel aria-label="Annuleren">&times;</button></div>`,
      () => {}
    );
  }

  // gedeelde onderbalk (keuzemenu / verplaats-prompt): vast onderin het zaalplan, nooit over de
  // stoelen heen. onAction(krijgt data-menu-waarde); data-cancel annuleert.
  _showActionBar(html, onAction) {
    let el = document.getElementById("sp2-action-bar");
    if (!el) {
      el = document.createElement("div");
      el.id = "sp2-action-bar";
      el.className = "sp2-actionbar";
      const wrap = this.canvasTarget.closest(".sp2-canvas-wrap") || this.canvasTarget.parentElement;
      wrap.appendChild(el);
    }
    el.innerHTML = html;
    el.hidden = false;
    el.onclick = (e) => {
      const b = e.target.closest("[data-menu],[data-cancel]");
      if (!b) return;
      e.stopPropagation();
      if (b.hasAttribute("data-cancel")) { this.cancelPickup(); return; }
      onAction(b.dataset.menu);
    };
  }

  _hideActionBar() {
    const el = document.getElementById("sp2-action-bar");
    if (el) { el.hidden = true; el.innerHTML = ""; el.onclick = null; }
  }

  // touch: keuzemenu bij een tik op je eigen stoel — als zwevende POPOVER bij de stoel (met een pijltje
  // dat naar de stoel wijst) i.p.v. een onderbalk over de bediening heen. Context (rij/stoel/rang) +
  // knoppen: verplaats één stoel of de hele rij. Sluit bij een tik erbuiten.
  showSeatMenu(seatId, blockIds) {
    if (this.busy) return;
    const seat = this.spike.seats.find((s) => s.id === seatId);
    if (!seat) return;
    const rank = this.ranksById[seat.rank_id];
    const ctx = `Rij ${seat.row_label} · stoel ${seat.nr}${rank ? ` · ${rank.name}` : ""}`;
    const rowBtn = (blockIds && blockIds.length > 1)
      ? `<button type="button" class="sp2-sm-btn" data-menu="row">Hele rij (${blockIds.length})</button>` : "";
    const el = this._ensureSeatMenu();
    el.innerHTML =
      `<div class="sp2-sm-ctx"><span class="sp2-sm-dot" style="background:${rank ? rank.color : "#6705bc"}"></span>${ctx}</div>` +
      `<button type="button" class="sp2-sm-btn" data-menu="seat">Verplaats deze stoel</button>${rowBtn}` +
      `<span class="sp2-sm-arrow"></span>`;
    el.hidden = false;
    el.onclick = (e) => {
      const b = e.target.closest("[data-menu]");
      if (!b) return;
      e.stopPropagation();
      const action = b.dataset.menu;
      this._hideSeatMenu();
      if (action === "seat") this.pickup([seatId], "seat");
      else if (action === "row") this.pickup(blockIds.slice(), "row");
    };
    this._placeSeatMenu(el, seat);
  }

  _ensureSeatMenu() {
    let el = document.getElementById("sp2-seat-menu");
    if (!el) {
      el = document.createElement("div");
      el.id = "sp2-seat-menu";
      el.className = "sp2-seatmenu";
      const wrap = this.canvasTarget.closest(".sp2-canvas-wrap") || this.canvasTarget.parentElement;
      wrap.appendChild(el);
    }
    return el;
  }

  _hideSeatMenu() {
    const el = document.getElementById("sp2-seat-menu");
    if (el) { el.hidden = true; el.innerHTML = ""; el.onclick = null; }
    if (this._smDismiss) { document.removeEventListener("pointerdown", this._smDismiss, true); this._smDismiss = null; }
  }

  // zet de popover boven de stoel (of eronder als er bovenaan geen ruimte is), horizontaal gecentreerd
  // op de stoel en binnen de canvas geklemd; het pijltje wijst exact naar de stoel.
  _placeSeatMenu(el, seat) {
    const p = this.spike.seatScreenPos(seat);
    const wrap = el.offsetParent || el.parentElement;
    const W = wrap ? wrap.clientWidth : 0, H = wrap ? wrap.clientHeight : 0;
    const pw = el.offsetWidth, ph = el.offsetHeight, m = 8, gap = 12;
    const above = p.y - ph - gap >= m;
    const top = above ? p.y - ph - gap : p.y + gap;
    const left = Math.max(m, Math.min(p.x - pw / 2, W - pw - m));
    el.classList.toggle("is-below", !above);
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    const arrow = el.querySelector(".sp2-sm-arrow");
    if (arrow) arrow.style.left = `${Math.round(Math.max(14, Math.min(p.x - left, pw - 14)))}px`;
    if (!this._smDismiss) {
      this._smDismiss = (e) => { if (!el.contains(e.target)) this._hideSeatMenu(); };
      setTimeout(() => document.addEventListener("pointerdown", this._smDismiss, true), 0);
    }
  }

  // plaats het zwevende menu naast een stoel, binnen de canvas geklemd, en sluit bij een tik erbuiten
  _placePicker(el, seatId) {
    const seat = this.spike.seats.find((s) => s.id === seatId);
    const p = this.spike.seatScreenPos(seat);
    const wrap = el.offsetParent || el.parentElement;
    const W = wrap ? wrap.clientWidth : 0, H = wrap ? wrap.clientHeight : 0;
    const pw = el.offsetWidth, ph = el.offsetHeight, m = 8, gap = 14;
    const left = Math.max(m, Math.min(p.x + gap, W - pw - m));
    const top = Math.max(m, Math.min(p.y - ph / 2, H - ph - m));
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    if (!this._gpDismiss) {
      this._gpDismiss = (e) => { if (!el.contains(e.target)) this._hideGroupPicker(); };
      setTimeout(() => document.addEventListener("pointerdown", this._gpDismiss, true), 0);
    }
  }

  // keuzemenu: welke van je groepen verplaats je hierheen? Verschijnt naast de aangeklikte plek.
  _showGroupPicker(groups, rank, seatId) {
    const el = document.getElementById("sp2-group-picker");
    if (!el) { this._tryMoveGroup(groups[0].ids, rank, seatId); return; } // vangnet: geen UI -> eerste groep
    const label = (g) => {
      const parts = [];
      if (g.sectionName) parts.push(g.sectionName);
      if (g.rowLabel != null) parts.push(`rij ${g.rowLabel}`);
      const seats = g.range ? `stoel ${g.range}` : `${g.count} stoelen`;
      return `${parts.join(" · ")}${parts.length ? " · " : ""}${seats}`;
    };
    el.innerHTML =
      `<div class="sp2-gp-head">Welke stoelen verplaatsen?</div>` +
      groups.map((g, i) => `<button type="button" class="sp2-gp-item" data-gp="${i}">${label(g)} <span class="sp2-gp-count">${g.count}×</span></button>`).join("");
    el.querySelectorAll("[data-gp]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const g = groups[parseInt(btn.dataset.gp, 10)];
        this._hideGroupPicker();
        if (g) this._tryMoveGroup(g.ids, rank, seatId);
      });
    });
    el.hidden = false;
    this._placePicker(el, seatId);
  }

  _hideGroupPicker() {
    const el = document.getElementById("sp2-group-picker");
    if (el) { el.hidden = true; el.innerHTML = ""; }
    if (this._gpDismiss) { document.removeEventListener("pointerdown", this._gpDismiss, true); this._gpDismiss = null; }
  }

  // Meng-loep (meerdere rangen) naar een vak gesleept: laat de gebruiker kiezen welke rang mee gaat.
  // De spike heeft al bepaald welke rangen dáár passen; elke optie draagt een commit() die de move doet.
  showMagRankChoice(options, secName) {
    const el = document.getElementById("sp2-group-picker");
    if (!el) { if (options[0]) options[0].commit(); return; } // vangnet: geen UI -> eerste optie
    const head = secName ? `Welke stoelen naar ${this._esc(secName)}?` : "Welke stoelen verplaatsen?";
    el.innerHTML =
      `<div class="sp2-gp-head">${head}</div>` +
      options.map((o, i) => {
        if (o.all) {
          return `<button type="button" class="sp2-gp-item" data-gp="${i}">` +
            `<span class="sp2-gp-left"><span class="sp2-gp-dot sp2-gp-dot-all"></span>Alle stoelen</span>` +
            `<span class="sp2-gp-count">${o.count}×</span></button>`;
        }
        const rank = this.ranksById[o.rankId];
        const name = this._esc(rank ? rank.name : "Rang");
        const color = rank ? rank.color : "#6705bc";
        return `<button type="button" class="sp2-gp-item" data-gp="${i}">` +
          `<span class="sp2-gp-left"><span class="sp2-gp-dot" style="background:${color}"></span>${name}</span>` +
          `<span class="sp2-gp-count">${o.count}×</span></button>`;
      }).join("");
    el.querySelectorAll("[data-gp]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const o = options[parseInt(btn.dataset.gp, 10)];
        this._hideGroupPicker();
        if (o) o.commit();
      });
    });
    el.hidden = false;
    this._placePickerCentered(el);
  }

  _placePickerCentered(el) {
    const wrap = el.offsetParent || el.parentElement;
    const W = wrap ? wrap.clientWidth : 0, H = wrap ? wrap.clientHeight : 0;
    el.style.left = `${Math.round(Math.max(8, (W - el.offsetWidth) / 2))}px`;
    el.style.top = `${Math.round(Math.max(8, (H - el.offsetHeight) / 2))}px`;
    if (!this._gpDismiss) {
      this._gpDismiss = (e) => { if (!el.contains(e.target)) this._hideGroupPicker(); };
      setTimeout(() => document.addEventListener("pointerdown", this._gpDismiss, true), 0);
    }
  }

  // terug naar de automatische (beste) toewijzing: server geeft je stoelen vrij en her-claimt
  // hetzelfde aantal via de DP. Handig als je na wat schuiven vastloopt door beperkte ruimte.
  async resetSeats() {
    if (this.busy) return;
    this.setBusy(true);
    try {
      const resp = await fetch(`/shopping_cart/reset_seats?uuid=${encodeURIComponent(this.uuidValue)}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
      });
      if (resp.redirected) { window.location.href = resp.url; return; }
      if (resp.ok && (resp.headers.get("content-type") || "").includes("application/json")) {
        this.applySeatData(await resp.json());
        this.showResetBtn(false); // terug op de automatische toewijzing -> knop weer verbergen
        this.showFlash("Beste plekken hersteld", "ok");
        this._zoomToMineSection(); // naar het vak waar je herstelde stoelen nu staan
        return;
      }
      const txt = (await resp.text()) || "";
      if (resp.status === 410 || txt === "EXPIRED") { window.location.href = "/agenda"; return; }
      if (txt === "failed_out_of_stock") { this.warn("Er is niet genoeg ruimte om automatisch opnieuw toe te wijzen."); return; }
      this.error("Herstellen is niet gelukt. Probeer het opnieuw.");
    } catch (e) {
      this.error("Er is een onverwachte fout opgetreden. Probeer het opnieuw.");
    } finally {
      this.setBusy(false);
    }
  }

  // zoom naar het vak waar de meeste van je eigen stoelen staan (na een herstel/auto-toewijzing),
  // zodat je meteen ziet waar je nu zit i.p.v. in een leeg/ander vak te blijven hangen.
  _zoomToMineSection() {
    if (!this.spike) return;
    const counts = {};
    this.spike.seats.forEach((s) => { if (s.state === "mine") counts[s.section_id] = (counts[s.section_id] || 0) + 1; });
    let best = null, bestN = 0;
    Object.keys(counts).forEach((sid) => { if (counts[sid] > bestN) { bestN = counts[sid]; best = sid; } });
    if (best != null) this.spike.zoomToSection(parseInt(best, 10));
  }

  // moves worden GEQUEUED en sequentieel verstuurd. Bij snel achter elkaar slepen mag een move niet
  // worden gedropt (dan liep de optimistische client-staat vóór op de server -> verkeerde vinkjes/errors).
  // Elke move's server-delta raakt alleen z'n eigen stoelen, dus de optimistische staat van nog-wachtende
  // moves blijft intact.
  moveSeats(fromIds, toId, okMsg = null) {
    (this._moveQueue = this._moveQueue || []).push({ fromIds, toId, okMsg });
    this._drainMoveQueue();
  }

  // verplaats naar een VAK: de server kiest de beste stoelen daar (rij-prioriteit-DP). Zelfde queue.
  moveSeatsToSection(fromIds, sectionId, okMsg = null) {
    (this._moveQueue = this._moveQueue || []).push({ fromIds, sectionId, okMsg });
    this._drainMoveQueue();
  }

  async _drainMoveQueue() {
    if (this._draining) return;
    this._draining = true;
    this.setBusy(true);
    try {
      while (this._moveQueue && this._moveQueue.length) {
        const m = this._moveQueue.shift();
        await this._doMove(m.fromIds, m.toId, m.okMsg, m.sectionId);
      }
    } finally {
      this._draining = false;
      this.setBusy(false);
    }
  }

  async _doMove(fromIds, toId, okMsg = null, sectionId = null) {
    // vangnet: bij een gelijktijdige verplaatsing kan de server op een rij-lock wachten (MariaDB tot
    // ~50s). Zonder timeout blijft de fetch hangen. Na 15s afbreken -> de UI herstelt.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 15000);
    try {
      const body = new URLSearchParams();
      fromIds.forEach((id) => body.append("from_seat_ids[]", id));
      // section_id = laat de server de beste stoelen in dat vak kiezen; anders een concrete plek
      if (sectionId != null) body.append("section_id", sectionId);
      else body.append("to_seat_id", toId);
      const resp = await fetch(`/shopping_cart/move_seat?uuid=${encodeURIComponent(this.uuidValue)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
        },
        body: body.toString(),
        signal: ctrl.signal,
      });
      if (resp.ok) {
        // succes: de server stuurt de verse zaalplan-data direct mee (één round-trip, geen
        // tweede fetch) zodat de verplaatsing meteen bevestigt zonder "lag"
        if ((resp.headers.get("content-type") || "").includes("application/json")) {
          this.applySeatData(await resp.json());
          this.showResetBtn(true); // default-toewijzing aangepast -> herstel-knop tonen
          if (okMsg) this.showFlash(okMsg, "ok");
          return;
        }
        const txt = (await resp.text()) || "";
        if (txt === "success") { await this.refreshSeats(); this.showResetBtn(true); if (okMsg) this.showFlash(okMsg, "ok"); return; } // fallback (oude tekst-respons)
        if (txt === "EXPIRED") { window.location.href = "/agenda"; return; }
        this.warn(this.failureMessage(txt));
        return;
      }
      const txt = (await resp.text()) || "";
      if (resp.status === 410 || txt === "EXPIRED") { window.location.href = "/agenda"; return; }
      if (resp.status === 409) {
        // opgepakte stoel(en) blijven opgepakt, zodat je meteen een andere plek kunt kiezen
        if (txt === "NO_FIT") this.warn("Hier passen niet al je stoelen aaneengesloten. Kies een plek met genoeg ruimte.");
        else if (txt.indexOf("SEAT_TAKEN") === 0) this.warn("Deze plek is net door iemand anders geclaimd. Kies een andere plaats.");
        else this.warn("Deze verplaatsing kon niet worden geplaatst. Probeer een andere plek.");
        await this.refreshSeats();
        return;
      }
      if (resp.status === 404) this.error("Deze voorstelling of stoel kon niet worden gevonden.");
      else this.error("Je verplaatsing kon niet worden verwerkt.");
      // 404/422/onverwacht: de client-staat is uit de pas gelopen met de DB -- de server verwierp de
      // from_seat_ids (server-side een ArgumentError "bron hoort niet bij deze cart" -> 422). Zonder
      // resync stuurt elke volgende move dezelfde stale ids en blijf je in de 422 hangen tot een
      // handmatige refresh. We herstellen daarom zoals een volledige reload dat doet: eventuele pickup
      // opheffen (anders blijven z'n ids terugkomen) en verse serverstaat ophalen. refreshSeats stuurt
      // bij een echt verlopen cart door naar /agenda.
      if (this._pickup) this.cancelPickup();
      await this.refreshSeats().catch(() => {});
    } catch (e) {
      if (e && e.name === "AbortError") {
        this.error("De verplaatsing duurde te lang (mogelijk claimt iemand anders dezelfde plek). Probeer het opnieuw.");
      } else {
        this.error("Er is een onverwachte fout opgetreden. Probeer het opnieuw.");
      }
      if (this._pickup) this.cancelPickup();
      await this.refreshSeats().catch(() => {}); // verse staat ophalen zodat je weer verder kunt
    } finally {
      clearTimeout(timer);
    }
  }

  // verse stoel-status + tickets ophalen en in-place renderen (zonder animatie/reload)
  async refreshSeats() {
    const resp = await fetch(`/shopping_cart/seatplan_data?uuid=${encodeURIComponent(this.uuidValue)}`, {
      headers: { Accept: "application/json" },
    });
    // verlopen cart e.d.: de server doet een HTML-redirect (bv. naar /agenda). fetch volgt die,
    // dus resp.ok is true maar de body is HTML -> ga naar die pagina i.p.v. op json() te crashen.
    if (resp.redirected) { window.location.href = resp.url; return; }
    if (!resp.ok) { window.location.reload(); return; } // vangnet: val terug op de oude flow
    this.applySeatData(await resp.json());
  }

  // verse zaalplan-data toepassen. Twee vormen:
  //  - volledig (data.seats): de hele stoelenlijst vervangen (refresh/reset).
  //  - delta (data.seats_delta): alleen de gewijzigde stoelen in de bestaande lijst mergen (na een
  //    verplaatsing) — scheelt ~400kb per call, want de client heeft alle stoelen al in het geheugen.
  applySeatData(data) {
    if (!data) return;
    if (Array.isArray(data.seats)) {
      this.spike.seats = data.seats;
    } else if (Array.isArray(data.seats_delta)) {
      const byId = new Map(this.spike.seats.map((s) => [s.id, s]));
      data.seats_delta.forEach((d) => {
        const cur = byId.get(d.id);
        if (cur) Object.assign(cur, d);
        else { this.spike.seats.push(d); byId.set(d.id, d); }
      });
    } else {
      return; // niets bruikbaars
    }
    this.spike.orphanIds = new Set((data.orphans || []).map((o) => o.id)); // rode markering bijwerken
    this.spike.render();
    this.updateOrphans(data.orphans || []);
    const mine = this.spike.seats.find((s) => s.state === "mine");
    this.mineSectionId = mine ? mine.section_id : this.mineSectionId;
    this.renderTickets(data.my_tickets || [], data.my_tickets_html, data.total);
  }

  // "Jouw selectie" live bijwerken. De opmaak komt van de server-partial (my_tickets_html)
  // zodat de refresh gegarandeerd identiek is aan de eerste render; het aantal-badge en de
  // focus-highlight zetten we hier bij.
  renderTickets(tickets, html, total) {
    const box = document.getElementById("sp2-my-tickets");
    if (box && html != null) box.innerHTML = html;
    this._hoverSeatId = null;
    const count = document.getElementById("sp2-ticket-count");
    if (count) count.textContent = tickets.reduce((n, t) => n + (t.count || 0), 0);
    // AT-1113: het bedrag ernaast hoort mee te lopen met de teller. Server-opgemaakt, dus
    // hier alleen neerzetten. Het element bestaat niet als er bij het laden nog geen
    // stoelen waren.
    const totaal = document.querySelector(".sp2-panel-total");
    if (totaal && total != null) totaal.textContent = total;
    this.updateFocusHighlight(); // herbouwde kaartjes: highlight-status direct terugzetten
  }

  failureMessage(code) {
    switch (code) {
      case "failed_no_seats_in_range": return "Geen stoelen gevonden.";
      case "failed_no_seats_with_matching_rank": return "Geen stoelen met de juiste rang gevonden.";
      case "failed_too_few_seats_available_on_this_row": return "Te weinig stoelen beschikbaar op deze plek.";
      case "failed_selection_keeps_one_single_seat_free": return "Uw keuze laat 1 stoel vrij, dit is niet mogelijk. Kies een andere plaats.";
      case "SEAT_TAKEN": return "Deze stoel is net door iemand anders geclaimd. Kies een andere plaats.";
      default: return "Je selectie kon niet worden geplaatst. Probeer een andere plek.";
    }
  }

  warn(message) { this.showFlash(message, "warn"); }
  error(message) { this.showFlash(message, "error"); }

  // vluchtige melding IN de canvas (bovenin, onder een eventueel orphan-balkje) i.p.v.
  // een toast rechtsboven het scherm — dichter bij waar de bezoeker kijkt
  showFlash(message, type = "warn") {
    const el = document.getElementById("sp2-flash");
    if (!el) { window.toastr ? window.toastr[type === "error" ? "error" : "warning"](message) : alert(message); return; }
    // onder de sectie-badges én onder een eventueel orphan-balkje plaatsen, zodat niets overlapt
    const badges = document.getElementById("sp2-section-tabs");
    const banner = document.getElementById("sp2-orphan-banner");
    const below = (n) => (n && !n.hidden && n.offsetParent) ? n.offsetTop + n.offsetHeight + 8 : 0;
    el.style.top = `${Math.max(below(badges), below(banner), 58)}px`;
    el.textContent = message;
    const variant = type === "error" ? "error" : type === "ok" ? "ok" : "warn";
    el.className = `sp2-flash sp2-flash--${variant} is-visible`;
    el.hidden = false;
    clearTimeout(this._flashTimer);
    // bevestigingen (ok) mogen kort; waarschuwingen/fouten (bv. "plek al bezet") langer zichtbaar houden
    this._flashTimer = setTimeout(() => { el.classList.remove("is-visible"); }, variant === "ok" ? 2500 : 3500);
  }

  // tooltip bij hover: rij + stoel + rang + prijs. Tegelijk lichten we de bijbehorende
  // regel in "Jouw selectie" op zodat je ziet welk ticket deze stoel is.
  showPopover(seat, x, y) {
    // op touch geen hover-info-popover: een tik triggert een synthetische mouseenter zonder mouseleave,
    // waardoor de popover bleef hangen. Op touch is er het stoel-keuzemenu (showSeatMenu) i.p.v. hover.
    if (this.spike && this.spike._touch) return;
    // tijdens grab-pannen geen tooltip tonen (alleen bij echte hover)
    if (this.spike && this.spike._stagePanning) return;
    this.linkSeatRow(seat.id);
    if (!this.hasPopoverTarget) return;
    const r = this.ranksById[seat.rankId] || { name: "", color: "#ccc" };
    const color = (this.spike && this.spike.colors && this.spike.colors[seat.rankId]) || r.color;
    const price = r.price_label ? `${r.price_from ? "vanaf " : ""}${r.price_label}` : "";
    const state = this.spike.seatState(seat.id);
    const orphan = this.spike.orphanIds.has(seat.id);
    const mine = state === "mine";
    const barColor = state === "taken" ? "#40464e" : color;
    const title = mine ? "Geselecteerd" : state === "free" ? "Vrije plek" : state === "taken" ? "Bezet" : r.name;

    // contextuele hint bij de stoel zelf i.p.v. in de rechterkolom
    let hint = "";
    if (orphan && mine) hint = `<div class="sp2-pop-hint warn">Deze stoel staat los van je andere stoelen. Sleep 'm naast je andere stoelen.</div>`;
    else if (orphan) hint = `<div class="sp2-pop-hint warn">Deze plek blijft over als losse stoel — die kan niemand meer los kopen. Sleep er een stoel naartoe of plaats je stoelen naast elkaar.</div>`;
    else if (mine) hint = `<div class="sp2-pop-hint">Sleep om deze stoel te verplaatsen · de greep rechts verplaatst je hele rij</div>`;
    else if (state === "free") hint = `<div class="sp2-pop-hint">Klik om je rij hierheen te verplaatsen (ook naar een ander vak) · of sleep een losse stoel hierheen</div>`;

    this.popoverTarget.innerHTML =
      `<div class="sp2-pop-top">
         ${seat.section ? `<div class="sp2-pop-col"><span class="sp2-pop-lbl">Sectie</span><span class="sp2-pop-val">${seat.section}</span></div>` : ""}
         <div class="sp2-pop-col"><span class="sp2-pop-lbl">Rij</span><span class="sp2-pop-val">${seat.rowLabel ?? "?"}</span></div>
         <div class="sp2-pop-col"><span class="sp2-pop-lbl">Stoel</span><span class="sp2-pop-val">${seat.nr ?? "?"}</span></div>
       </div>
       <div class="sp2-pop-bar" style="background:${barColor}">
         <div class="sp2-pop-status">${mine ? '<span class="sp2-pop-check">✓</span>' : ""}<div class="sp2-pop-titles"><span class="sp2-pop-title">${title}</span><span class="sp2-pop-sub">${r.name}${seat.wheelchair ? " · rolstoelplek" : ""}</span></div></div>
         ${price ? `<span class="sp2-pop-price">${price}</span>` : ""}
       </div>${hint}`;
    // eerst tonen zodat we de werkelijke afmeting kunnen meten, dan binnen de canvas klemmen
    this.popoverTarget.style.transform = "none";
    this.popoverTarget.style.display = "block";
    this.positionPopover(x, y);
  }

  // plaats de popover binnen de canvas-grenzen: gecentreerd + boven de stoel, maar
  // geklemd zodat 'ie niet wordt afgekapt, en naar onder geflipt als 'ie bovenaan niet past.
  positionPopover(x, y) {
    const wrap = this.popoverTarget.offsetParent || this.popoverTarget.parentElement;
    const W = wrap ? wrap.clientWidth : 0;
    const H = wrap ? wrap.clientHeight : 0;
    const pw = this.popoverTarget.offsetWidth;
    const ph = this.popoverTarget.offsetHeight;
    const m = 8;        // marge tot de rand
    const gap = 14;     // ruimte tussen stoel en kaart
    let left = x - pw / 2;
    left = Math.max(m, Math.min(left, W - pw - m));
    let top = y - ph - gap;                 // standaard: boven de stoel
    if (top < m) top = y + gap;             // past niet boven -> onder de stoel
    top = Math.max(m, Math.min(top, H - ph - m));
    this.popoverTarget.style.left = `${Math.round(left)}px`;
    this.popoverTarget.style.top = `${Math.round(top)}px`;
  }

  hidePopover() {
    if (this.hasPopoverTarget) this.popoverTarget.style.display = "none";
    this.linkSeatRow(null);
  }

  // kleine tooltip bij de rij-greep ("Rij verplaatsen"), binnen de canvas geklemd
  showTip(text, x, y) {
    const el = document.getElementById("sp2-tip");
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    const wrap = el.offsetParent || el.parentElement;
    const W = wrap ? wrap.clientWidth : 0;
    const tw = el.offsetWidth, th = el.offsetHeight;
    let left = x + 12; if (left + tw > W - 6) left = x - tw - 12;
    let top = y - th - 8; if (top < 6) top = y + 16;
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  }
  hideTip() { const el = document.getElementById("sp2-tip"); if (el) el.hidden = true; }

  // markeer de stoel-regel in de selectielijst die bij deze stoel hoort (of wis de markering)
  linkSeatRow(seatId) {
    const box = document.getElementById("sp2-my-tickets");
    if (!box) return;
    if (this._linkedRow) { this._linkedRow.classList.remove("is-linked"); this._linkedRow = null; }
    if (seatId == null) return;
    const row = box.querySelector(`[data-seat-id="${seatId}"]`);
    if (row) { row.classList.add("is-linked"); this._linkedRow = row; }
  }
}
